import {
  JudgmentResult,
  MeasureConfig,
  JudgmentResultType,
  DiscussionOverrides,
  BaseMeasureFields,
} from "./types.client";

/**
 * 모든 계측 타입 컨트롤러의 추상 베이스 클래스
 * @template T - 스키마 타입 (AcVoltageMeasure, DcVoltageMeasure 등)
 *               반드시 BaseMeasureFields를 확장해야 함
 */
export abstract class MeasureController<T extends BaseMeasureFields> {
  protected data: T;
  protected config: MeasureConfig;
  protected discussionOverrides?: DiscussionOverrides;

  constructor(
    initialData: Partial<T> = {},
    config: MeasureConfig = {},
    discussionOverrides?: DiscussionOverrides
  ) {
    this.config = config; // config 먼저 설정 (judge()에서 사용)
    this.discussionOverrides = discussionOverrides;
    this.data = this.getDefaultData();
    this.setData(initialData);
  }

  /** 기본 데이터 반환 (각 컨트롤러에서 구현) */
  protected abstract getDefaultData(): T;

  /** 판정 로직 구현 (각 컨트롤러에서 구현) */
  protected abstract judge(): JudgmentResult;

  /** 의견 생성 (각 컨트롤러에서 구현) */
  protected abstract generateDiscussion(result: JudgmentResultType): string;

  /**
   * 최종 discussion 결정
   * 1. discussionOverrides가 있으면 judgment에 맞는 코멘트 사용
   * 2. 없으면 generateDiscussion() fallback
   */
  protected getFinalDiscussion(result: JudgmentResultType): string {
    if (this.discussionOverrides) {
      const { common, good, caution, bad, na } = this.discussionOverrides;
      let finalText = common ?? "";

      switch (result) {
        case "GOOD":
          if (good) finalText += (finalText ? " " : "") + good;
          break;
        case "CAUTION":
          if (caution) finalText += (finalText ? " " : "") + caution;
          break;
        case "BAD":
          if (bad) finalText += (finalText ? " " : "") + bad;
          break;
        case "NA":
          if (na) finalText += (finalText ? " " : "") + na;
          break;
      }

      if (finalText) return finalText;
    }

    // fallback to hardcoded
    return this.generateDiscussion(result);
  }

  /** 데이터 설정 후 자동 판정 (수동 설정 시 스킵) */
  public setData(partial: Partial<T>): JudgmentResult {
    this.data = { ...this.data, ...partial };

    const isManuallySet = this.data.resultManuallySet === true;

    // 수동 판정이면 자동 판정 스킵
    if (isManuallySet) {
      const currentResult = this.data.result;
      const currentDiscussion = this.data.discussion;
      return {
        result: currentResult ?? "NA",
        discussion: currentDiscussion ?? "",
      };
    }

    const judgment = this.judge();
    // MeasureCommon의 result, discussion 필드 자동 업데이트
    this.data.result = judgment.result as T["result"];
    // getFinalDiscussion()으로 discussionOverrides 반영
    this.data.discussion = this.getFinalDiscussion(judgment.result);
    return {
      result: judgment.result,
      discussion: this.data.discussion ?? "",
    };
  }

  /** 특정 필드 값 설정 후 자동 판정 */
  public setValue<K extends keyof T>(key: K, value: T[K]): JudgmentResult {
    // TypeScript 제네릭 제약으로 인해 이중 타입 단언 필요
    const partial = { [key]: value } as unknown as Partial<T>;
    return this.setData(partial);
  }

  /** 설정 변경 */
  public setConfig(config: Partial<MeasureConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /** 현재 데이터 반환 */
  public getData(): T {
    return { ...this.data };
  }

  /** 현재 판정 결과만 반환 */
  public getJudgment(): JudgmentResult {
    return this.judge();
  }

  /** 데이터 유효성 검증 (옵션) */
  public validate(): boolean {
    return true; // 기본은 항상 유효, 필요 시 오버라이드
  }
}
